import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db, functions } from '../config/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { BoardStorage } from '../utils/storage';

// Simple event emitter for auth state changes
class AuthEventEmitter {
  private listeners: (() => void)[] = [];

  subscribe(listener: () => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  emit() {
    this.listeners.forEach(listener => listener());
  }
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin';
  isSuspended?: boolean;
  suspensionReason?: string;
  subscriptionStatus?: 'active' | 'expired' | 'trial';
  subscriptionType?: 'paid' | 'gift' | 'trial';
  subscriptionLabel?: string;
  subscriptionExpiry?: number;
  needs2FA?: boolean;
  has2FAEnabled?: boolean;
  badgeNumber?: string;
  title?: string;
  agency?: string;
  createdAt?: any;
  updatedAt?: any;
}

const SUPERADMIN_EMAILS = ['rob@taillonindustries.com', 'rtyo@icloud.com'];

class AuthService {
  private currentUser: User | null = null;
  private tempUser: User | null = null; // Partially authenticated user awaiting 2FA
  private pending2FASecret: string | null = null; // Secret generated during setup, awaiting verification
  private eventEmitter = new AuthEventEmitter();
  private firebaseUser: FirebaseUser | null = null;
  private authReady = false;

  constructor() {
    onAuthStateChanged(auth, async (user) => {
      this.firebaseUser = user;
      if (user) {
        // Fetch full profile
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const data = userDoc.data() as Partial<User>;

            // SUPERADMIN AUTO-PROMOTION
            if (user.email && SUPERADMIN_EMAILS.includes(user.email) && data.role !== 'admin') {
              console.log('Fibre: Auto-promoting superadmin account...', user.email);
              await setDoc(userDocRef, { role: 'admin' }, { merge: true });
              data.role = 'admin';
            }

            this.currentUser = {
              id: user.uid,
              name: data.name || user.displayName || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              role: data.role || 'user',
              has2FAEnabled: data.has2FAEnabled || false,
              ...data
            };
          } else {
            // Fallback if doc missing (shouldn't happen often)
            const isSuperadmin = user.email && SUPERADMIN_EMAILS.includes(user.email);
            if (isSuperadmin) {
              console.log('Fibre: Creating missing superadmin doc...', user.email);
              await setDoc(userDocRef, {
                name: user.displayName || user.email?.split('@')[0] || 'Admin',
                email: user.email,
                role: 'admin',
                createdAt: Date.now()
              }, { merge: true });
            }

            this.currentUser = {
              id: user.uid,
              name: user.displayName || user.email?.split('@')[0] || 'User',
              email: user.email || '',
              role: isSuperadmin ? 'admin' : 'user',
              has2FAEnabled: false
            };
          }
        } catch (e) {
          console.error('Error fetching user profile', e);
          this.currentUser = {
            id: user.uid,
            name: user.displayName || user.email?.split('@')[0] || 'User',
            email: user.email || '',
            role: 'user',
            has2FAEnabled: false
          };
        }
      } else {
        this.currentUser = null;
      }
      this.authReady = true;
      this.notifyAuthChange();
    });
  }

  subscribe(listener: () => void) {
    return this.eventEmitter.subscribe(listener);
  }

  /* private lastEmittedUser: string | null = null; */

  private notifyAuthChange() {
    /*
    const currentUserStr = JSON.stringify(this.currentUser);
    if (currentUserStr === this.lastEmittedUser) {
      return;
    }
    this.lastEmittedUser = currentUserStr;
    */
    this.eventEmitter.emit();
  }

  async login(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Fetch user profile from Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      let userData: Partial<User> = {};

      if (userDoc.exists()) {
        userData = userDoc.data() as Partial<User>;
      }

      if (userData.isSuspended) {
        await signOut(auth);
        throw new Error(`Account suspended: ${userData.suspensionReason || 'Contact support'}`);
      }

      const isSuperadmin = firebaseUser.email && SUPERADMIN_EMAILS.includes(firebaseUser.email);
      const user: User = {
        id: firebaseUser.uid,
        name: userData.name || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || (isSuperadmin ? 'Admin' : 'User'),
        email: firebaseUser.email || '',
        role: isSuperadmin ? 'admin' : (userData.role || 'user'),
        has2FAEnabled: userData.has2FAEnabled || false,
        ...userData
      };

      // If 2FA is enabled, hold user in temp state until verification
      if (user.has2FAEnabled) {
        this.tempUser = user;
        user.needs2FA = true;
        // Do NOT set currentUser — user is not fully authenticated yet
        return user;
      }

      this.currentUser = user;
      this.notifyAuthChange();
      return this.currentUser;
    } catch (error: any) {
      throw error;
    }
  }


  async register(name: string, email: string, password: string, promoCode?: string): Promise<User> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Check for valid promo code
    const hasValidPromo = promoCode === '1601';
    const isSuperadmin = user.email && SUPERADMIN_EMAILS.includes(user.email);

    const newUser: User = {
      id: user.uid,
      name: name,
      email: user.email || '',
      role: isSuperadmin ? 'admin' : 'user', // Grant admin immediately if email matches
      needs2FA: false,
      has2FAEnabled: false,
      subscriptionStatus: hasValidPromo ? 'active' : 'trial',
      subscriptionType: hasValidPromo ? 'gift' : 'trial',
      subscriptionLabel: hasValidPromo ? 'Full Access (Promo)' : '14-Day Free Trial',
      subscriptionExpiry: hasValidPromo
        ? Date.now() + (365 * 24 * 60 * 60 * 1000 * 100)
        : Date.now() + (14 * 24 * 60 * 60 * 1000), // 14 days for new users
      createdAt: Date.now()
    };

    // Create user document in Firestore
    try {
      await setDoc(doc(db, 'users', user.uid), newUser);
    } catch (dbError: any) {
      console.error('Firestore user creation failed:', dbError);
      // Optional: Clean up auth user if DB creation fails
      // await user.delete(); 
      throw new Error(`Failed to create profile: ${dbError.message}`);
    }

    this.currentUser = newUser;
    this.notifyAuthChange();
    return this.currentUser;
  }

  async logout(): Promise<void> {
    await signOut(auth);
    this.currentUser = null;
    this.tempUser = null;
    this.pending2FASecret = null;
    // Clear cached board data from the previous session
    BoardStorage.getInstance().clearCache();
    this.notifyAuthChange();
  }

  // RevenueCat integration hook would go here
  async getSubscriptionStatus() {
    // Retrieve status from RevenueCat or custom backend
    return 'trial';
  }

  getCurrentUser(): User | null {
    return this.currentUser;
  }

  isAuthenticated(): boolean {
    return this.currentUser !== null;
  }

  isAdmin(): boolean {
    return this.currentUser?.role === 'admin';
  }

  isAuthReady(): boolean {
    return this.authReady;
  }

  async updateUserProfile(profile: Partial<User>): Promise<void> {
    if (!this.currentUser) return;

    // Whitelist safe fields to prevent privilege escalation
    const SAFE_FIELDS = ['name', 'badgeNumber', 'title', 'agency'] as const;
    const safeProfile: Record<string, any> = {};
    for (const key of SAFE_FIELDS) {
      if (key in profile) {
        safeProfile[key] = profile[key];
      }
    }

    this.currentUser = {
      ...this.currentUser,
      ...safeProfile
    };

    // Save to Firestore user document
    try {
      await setDoc(doc(db, 'users', this.currentUser.id), safeProfile, { merge: true });
    } catch (e) {
      console.error('Failed to update user profile in Firestore', e);
    }

    this.notifyAuthChange();
  }

  /**
   * Change the current user's password.
   * Requires re-authentication with the current password for security.
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!this.firebaseUser || !this.firebaseUser.email) {
      throw new Error('No user logged in');
    }

    // Re-authenticate before password change (Firebase requirement)
    const credential = EmailAuthProvider.credential(this.firebaseUser.email, currentPassword);
    await reauthenticateWithCredential(this.firebaseUser, credential);

    // Update the password
    await updatePassword(this.firebaseUser, newPassword);
  }

  // --- TOTP-based 2FA ---

  /**
   * Generate a TOTP secret and QR code URL for the user to scan.
   * Called from the 2FA setup page.
   */
  async setup2FA(): Promise<{ secret: string; qrCode: string }> {
    if (!this.currentUser) throw new Error('Must be logged in');

    // Generate a random base32-like secret (16 chars)
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let secret = '';
    for (let i = 0; i < 16; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Store the pending secret until the user verifies it
    this.pending2FASecret = secret;

    // Generate a QR code URL via GoQR / QRServer (Standard replacement for deprecated Google Charts)
    const issuer = encodeURIComponent('Fibre');
    const account = encodeURIComponent(this.currentUser.email);
    const otpauthUri = `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}&digits=6&period=30`;
    const qrCode = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(otpauthUri)}`;

    return { secret, qrCode };
  }

  /**
   * Verify the TOTP code during setup and enable 2FA on the account.
   * Uses a Cloud Function for real server-side TOTP validation.
   */
  async enable2FA(code: string): Promise<boolean> {
    if (!this.currentUser) throw new Error('Must be logged in');
    if (!this.pending2FASecret) throw new Error('2FA setup not started');

    // Validate the code format
    if (!/^\d{6}$/.test(code)) return false;

    try {
      const setup2FAVerify = httpsCallable(functions, 'setup2FAVerify');
      const result = await setup2FAVerify({ code, secret: this.pending2FASecret });
      const data = result.data as { valid: boolean };

      if (!data.valid) return false;

      this.currentUser.has2FAEnabled = true;
      this.pending2FASecret = null;
      this.notifyAuthChange();
      return true;
    } catch (error) {
      console.error('Failed to enable 2FA:', error);
      return false;
    }
  }

  /**
   * Verify a TOTP code during login for users with 2FA enabled.
   * Uses a Cloud Function for real server-side TOTP validation.
   * On success, promotes the tempUser to a full session.
   */
  async verify2FA(code: string): Promise<boolean> {
    if (!this.tempUser) return false;

    // Validate the code format
    if (!/^\d{6}$/.test(code)) return false;

    try {
      const verify2FACode = httpsCallable(functions, 'verify2FACode');
      const result = await verify2FACode({ userId: this.tempUser.id, code });
      const data = result.data as { valid: boolean };

      if (!data.valid) return false;

      this.currentUser = this.tempUser;
      this.tempUser = null;
      this.notifyAuthChange();
      return true;
    } catch (error) {
      console.error('2FA verification failed:', error);
      return false;
    }
  }

  /**
   * Disable 2FA on the current account.
   */
  async disable2FA(): Promise<void> {
    if (!this.currentUser) throw new Error('Must be logged in');

    try {
      await updateDoc(doc(db, 'users', this.currentUser.id), {
        has2FAEnabled: false,
        twoFactorSecret: null
      });

      this.currentUser.has2FAEnabled = false;
      this.notifyAuthChange();
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      throw new Error('Failed to disable 2FA');
    }
  }

  /**
   * Get the temporarily stored user awaiting 2FA verification.
   * Returns null if no user is pending 2FA.
   */
  getTempUser(): User | null {
    return this.tempUser;
  }
}

export const authService = new AuthService();